<script setup lang="ts">
/**
 * Sol · pazarlama paneli — FW-126 KAPSAM B, veri-gudumlu blok listesi.
 * design/current/Auth v2.dc.html satir 30-79 karsiligi: blok tipleri
 * (image/headline/modules/checklist/quote) `content` prop'undan gelir,
 * hicbir metin bu dosyada SABIT DEGILDIR (bkz. resolveWelcomeBlocks.ts).
 *
 * Her blok tipi kendi kucuk bilesenine ayrildi (components/welcome/*) —
 * hepsi tek dosyada olsaydi eslint.config.js'in `vue/max-lines-per-block`
 * kurali (style icin 100 satir) asilirdi; ayrica her blok tek basina
 * anlasilir ve test edilebilir kaliyor.
 *
 * `gorsel` (FW-131 KAPSAM B) — design/current/Auth v2.dc.html props
 * semasi: verilmezse true, image blogu kalir. resolveWelcomeBlocks.ts'e
 * uygulama adapter'indan aktarilir; duzen mantigi burada TEKRARLANMAZ.
 */
import type { ResolvedWelcomeContent } from './resolveWelcomeBlocks'
import WelcomeImageBlock from './welcome/WelcomeImageBlock.vue'
import WelcomeHeadlineBlock from './welcome/WelcomeHeadlineBlock.vue'
import WelcomeModulesBlock from './welcome/WelcomeModulesBlock.vue'
import WelcomeChecklistBlock from './welcome/WelcomeChecklistBlock.vue'
import WelcomeQuoteBlock from './welcome/WelcomeQuoteBlock.vue'
import AuthBrandMark from './AuthBrandMark.vue'

// FW-224 — i18n ve seed secimi uygulamada kalir; UI yalniz cozulmus metni render eder.
defineProps<{ content: ResolvedWelcomeContent; brand: string; showChecklistMarkers?: boolean }>()
</script>

<template>
  <aside
    class="fw-auth-welcome"
    data-testid="auth-welcome-panel"
  >
    <!-- FW-145 tur 2 — design/current/Auth v2.dc.html satir 32-33: sol
         panelin arka planindaki yumusak dairesel isimalar. Onceden HIC
         YOKTU, panel duz lacivertti (koordinator bulgusu). aria-hidden:
         salt dekoratif, ekran okuyucuya anlam tasimaz. -->
    <div
      class="fw-auth-welcome__glow fw-auth-welcome__glow--bottom-right"
      aria-hidden="true"
    />
    <div
      class="fw-auth-welcome__glow fw-auth-welcome__glow--top-left"
      aria-hidden="true"
    />

    <div class="fw-auth-welcome__brand">
      <!-- FW-145 tur 2 — design/current/Auth v2.dc.html satir 35:
           ./brand/logo-mark-cobalt.svg. Onceden duz "FW" metniydi
           (koordinator bulgusu); artik tasarimin kendi marka isaretiyle
           birebir (packages/tokens --fw-primary rengiyle boyanir). -->
      <span
        class="fw-auth-welcome__mark"
        aria-hidden="true"
      >
        <AuthBrandMark />
      </span>
      <span>{{ brand }}</span>
    </div>

    <div class="fw-auth-welcome__blocks">
      <template
        v-for="(block, index) in content.blocks"
        :key="index"
      >
        <WelcomeImageBlock
          v-if="block.kind === 'image'"
          :alt="block.alt"
          :spec="block.spec"
        />
        <WelcomeHeadlineBlock
          v-else-if="block.kind === 'headline'"
          :kicker="block.kicker"
          :title="block.title"
          :sub="block.sub"
        />
        <WelcomeModulesBlock
          v-else-if="block.kind === 'modules'"
          :items="block.items"
        />
        <WelcomeChecklistBlock
          v-else-if="block.kind === 'checklist'"
          :items="block.items"
          :show-markers="showChecklistMarkers"
        />
        <WelcomeQuoteBlock
          v-else-if="block.kind === 'quote'"
          :text="block.text"
          :name="block.name"
          :role="block.role"
          :initials="block.initials"
        />
      </template>
    </div>

    <ul
      v-if="content.proof.length > 0"
      class="fw-auth-welcome__proof"
    >
      <li
        v-for="line in content.proof"
        :key="line"
      >
        <!-- FW-145 tur 2 — design/current/Auth v2.dc.html satir 76: her
             kanit satirindan once 5x5 nokta isareti. Onceden yoktu
             (koordinator bulgusu: "iki satir, madde isaretli" tasarimda,
             canlida "tek satir, isaretsiz"). -->
        <span
          class="fw-auth-welcome__proof-dot"
          aria-hidden="true"
        />
        {{ line }}
      </li>
    </ul>
  </aside>
</template>

<style scoped>
.fw-auth-welcome {
  /* position:relative — glow dairelerinin (position:absolute) konum
     referansi VE brand/blocks/proof'un onlarin USTUNDE boyanmasi icin
     sart (bkz. asagidaki .fw-auth-welcome__glow notu). */
  position: relative;
  background: var(--fw-cobalt-900);
  color: #fff;
  padding: clamp(24px, 4vh, 44px) clamp(28px, 4.4vw, 52px) clamp(20px, 3.6vh, 36px);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* FW-145 tur 2 — Auth v2.dc.html:32-33 ile birebir. position:absolute
   boyama sirasinda static kardeslerin USTUNE cikar; bu yuzden brand/
   blocks/proof'a asagida ayrica position:relative verildi. */
.fw-auth-welcome__glow {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}

.fw-auth-welcome__glow--bottom-right {
  inset: auto -140px -180px auto;
  width: 520px;
  height: 520px;
  background: rgba(255, 255, 255, 0.05);
}

.fw-auth-welcome__glow--top-left {
  inset: -120px auto auto -90px;
  width: 300px;
  height: 300px;
  background: rgba(255, 255, 255, 0.04);
}

.fw-auth-welcome__brand {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  flex: none;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em; /* FW-145 — Auth v2.dc.html:36, olculdu: 'normal' idi */
}

.fw-auth-welcome__mark {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: var(--fw-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: none;
}

.fw-auth-welcome__blocks {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: clamp(14px, 2.6vh, 28px);
  padding: clamp(14px, 2.6vh, 28px) 0;
  overflow: hidden;
}

.fw-auth-welcome__proof {
  position: relative;
  flex: none;
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.62);
  list-style: none;
  margin: 0;
  padding: 0;
}

.fw-auth-welcome__proof li {
  display: flex;
  align-items: center;
  gap: 7px;
}

.fw-auth-welcome__proof-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
  flex: none;
}
</style>
